import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatTRY, resolveImageUrl } from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import OccupancyChart from "@/components/OccupancyChart";
import {
    TrendingUp, Calendar as CalendarIcon, Wallet, Users, ClipboardList,
    BedDouble, Star, ArrowUpRight, LoaderCircle,
} from "lucide-react";

const guestName = (b) => {
    const g = b?.guest || {};
    const n = [g.first_name, g.last_name].filter(Boolean).join(" ").trim();
    return n || b?.customer_name || "—";
};
const guestCount = (b) => (Number(b?.adults) || 0) + (Number(b?.children) || 0) || Number(b?.guests) || 1;

const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
};

const StatCard = ({ label, value, hint, icon: Icon, color = "emerald", testid }) => (
    <div data-testid={testid} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">{label}</div>
                <div className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
                {hint && <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{hint}</div>}
            </div>
            <div className={`p-2.5 rounded-xl bg-${color}-100 dark:bg-${color}-950/40 text-${color}-600 dark:text-${color}-400`}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
    </div>
);

const StatusBadge = ({ status }) => {
    const map = {
        pending: { bg: "bg-amber-100 dark:bg-amber-950/30", txt: "text-amber-700 dark:text-amber-300", label: "Beklemede" },
        confirmed: { bg: "bg-emerald-100 dark:bg-emerald-950/30", txt: "text-emerald-700 dark:text-emerald-300", label: "Onaylı" },
        completed: { bg: "bg-sky-100 dark:bg-sky-950/30", txt: "text-sky-700 dark:text-sky-300", label: "Tamamlandı" },
        cancelled: { bg: "bg-rose-100 dark:bg-rose-950/30", txt: "text-rose-700 dark:text-rose-300", label: "İptal" },
    };
    const s = map[status] || { bg: "bg-slate-100", txt: "text-slate-600", label: status || "—" };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.bg} ${s.txt}`}>{s.label}</span>;
};

export default function HotelDashboardPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate("/h/login");
        const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
        if (!HOTEL_ROLES.includes(user.role)) {
            return navigate("/admin");
        }
        api.get("/hotel/dashboard")
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [user, authLoading, navigate]);

    if (loading || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
                <LoaderCircle className="h-8 w-8 text-emerald-500 animate-spin" />
            </div>
        );
    }

    const k = data.kpis || {};
    const hotel = data.hotel || {};

    return (
        <HotelLayout title="Dashboard" hotelName={hotel.name} hotelImage={hotel.image}>
            {/* Hero strip */}
            <div data-testid="hotel-hero" className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-600 text-white shadow-xl">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Hoş geldiniz</div>
                        <h2 className="font-display text-2xl font-bold mt-0.5">{user?.full_name}</h2>
                        <p className="text-sm opacity-90 mt-1">{hotel.name} · {hotel.destination} · {"★".repeat(hotel.stars || 0)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur text-xs font-bold uppercase tracking-wider">{user?.role?.replace("otel_", "")}</span>
                    </div>
                </div>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard testid="kpi-today-bookings" label="Bugün Rezervasyon" value={k.today_bookings} hint={`Bu hafta: ${k.week_bookings} · Bu ay: ${k.month_bookings}`} icon={ClipboardList} color="emerald" />
                <StatCard testid="kpi-occupancy" label="Bugün Doluluk" value={`%${k.occupancy_today_pct}`} hint={`${k.occupancy_today_booked} / ${k.occupancy_today_capacity} oda`} icon={BedDouble} color="sky" />
                <StatCard testid="kpi-month-revenue" label="Bu Ay Gelir" value={formatTRY(k.month_revenue)} hint={`Toplam: ${formatTRY(k.total_revenue)}`} icon={Wallet} color="amber" />
                <StatCard testid="kpi-adr" label="ADR (Ay)" value={formatTRY(k.adr)} hint="Ortalama gece fiyatı" icon={TrendingUp} color="purple" />
            </div>

            {/* Occupancy chart */}
            {user?.hotel_id && (
                <div className="mb-6">
                    <OccupancyChart hotelId={user.hotel_id} days={30} />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upcoming Check-ins */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><CalendarIcon className="h-4 w-4 text-emerald-500" /> Yaklaşan Check-in&apos;ler (7 gün)</h3>
                        <Link to="/h/bookings" className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Tümü <ArrowUpRight className="h-3 w-3" /></Link>
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
                        {(data.upcoming_checkins || []).length === 0 && (
                            <div className="p-8 text-center text-sm text-slate-500" data-testid="upcoming-empty">Önümüzdeki 7 gün içinde check-in yok.</div>
                        )}
                        {(data.upcoming_checkins || []).map((b) => (
                            <div key={b.id} data-testid={`upcoming-${b.id}`} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold flex-shrink-0">
                                        {guestName(b)[0]}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{guestName(b)}</div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400">{fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.rooms} oda · {guestCount(b)} kişi</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <StatusBadge status={b.status} />
                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{formatTRY(b.total_amount)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Bookings */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><Users className="h-4 w-4 text-sky-500" /> Son Rezervasyonlar</h3>
                        <Link to="/h/bookings" className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Tümü <ArrowUpRight className="h-3 w-3" /></Link>
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
                        {(data.recent_bookings || []).length === 0 && (
                            <div className="p-8 text-center text-sm text-slate-500" data-testid="recent-empty">Henüz rezervasyon yok.</div>
                        )}
                        {(data.recent_bookings || []).map((b) => (
                            <div key={b.id} data-testid={`recent-${b.id}`} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="h-10 w-10 rounded-full bg-sky-100 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 flex items-center justify-center font-bold flex-shrink-0">
                                        {guestName(b)[0]}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{guestName(b)}</div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400">{fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.rooms} oda</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <StatusBadge status={b.status} />
                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{formatTRY(b.total_amount)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Quick actions footer */}
            {k.pending_reviews > 0 && (
                <div className="mt-6 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 flex items-center gap-3" data-testid="pending-reviews-banner">
                    <Star className="h-5 w-5 text-amber-500" />
                    <div className="flex-1">
                        <div className="font-bold text-amber-900 dark:text-amber-200 text-sm">{k.pending_reviews} yorum onayınızı bekliyor</div>
                        <div className="text-xs text-amber-700 dark:text-amber-300">Misafir yorumlarını inceleyip yayınlayın.</div>
                    </div>
                    <Link to="/h/reviews" className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-colors">İncele</Link>
                </div>
            )}
        </HotelLayout>
    );
}
